import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PlatformDashboard from './admin/PlatformDashboard';
import BreweryDashboard from './admin/BreweryDashboard';
import UserDashboard from './user/UserDashboard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Button from '../components/ui/Button';
import { RefreshCw, LogOut, AlertTriangle, ShieldAlert } from '../lib/lucide-proxy';
import toast from 'react-hot-toast';
import { supabase } from '../config/supabase';

const DashboardManager = () => {
  const { profile, loading, signOut, user, fetchUserProfile } = useAuth();
  const [isRetrying, setIsRetrying] = useState(false);

  const handleForceRetry = async () => {
    setIsRetrying(true);
    const toastId = toast.loading("Executando reparo forçado...");

    try {
        // Chama diretamente a função V2 que é a mais poderosa
        const { data, error } = await supabase.rpc('force_admin_access_v2');
        
        if (error) throw error;
        
        if (data?.success) {
            toast.success("Perfil recuperado com sucesso!", { id: toastId });
            // Recarrega o perfil no contexto
            await fetchUserProfile(user);
        } else {
            throw new Error(data?.message || "Falha desconhecida");
        }
    } catch (e) {
        console.error("Repair failed:", e);
        toast.error("Falha no reparo: " + e.message, { id: toastId });
    } finally {
        setIsRetrying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 flex-col gap-4">
        <LoadingSpinner size="lg" />
        <p className="text-dark-500 text-sm animate-pulse">Carregando ambiente...</p>
      </div>
    );
  }

  // Se o usuário está logado (user existe) mas o perfil não carregou (profile null)
  if (user && !profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-soft max-w-md w-full text-center border border-yellow-100">
          <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-yellow-500" />
          </div>
          <h2 className="text-xl font-bold text-dark-900 mb-2">Perfil Desincronizado</h2>
          <p className="text-dark-500 mb-6 text-sm">
            Detectamos seu login ({user.email}), mas o perfil de usuário não foi carregado corretamente.
          </p>
          
          <div className="space-y-3">
            <Button 
              variant="primary" 
              onClick={handleForceRetry} 
              loading={isRetrying}
              className="w-full flex items-center justify-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reparar Perfil Agora
            </Button>
            
            <Button 
              variant="ghost" 
              onClick={signOut} 
              className="w-full flex items-center justify-center text-red-500 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>

          {user.email === 'doc@eidoc.com.br' && (
             <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs text-blue-700 flex items-center justify-center">
                <ShieldAlert className="w-4 h-4 mr-2" />
                Modo de Recuperação Admin V2
             </div>
          )}
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const renderDashboard = () => {
    switch (profile.role) {
      case 'admin':
        return <PlatformDashboard />;
      case 'brewery_owner':
        return <BreweryDashboard />;
      case 'customer':
      default:
        return <UserDashboard />;
    }
  };

  return (
    <div className="relative">
      {renderDashboard()}
    </div>
  );
};

export default DashboardManager;